/**
 * Commission Recipients (UI-2 v3 layout, BE-6a data)
 *
 * A recipient is a TC team member with a commission profile (type, bank details, active); name and email come from
 * the user. "Owes back" is commission paid out on invoices later voided, cleared by Recover / Write off.
 * Route: /accounting/commissions/recipients
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  IconButton, Tooltip, Switch, FormControlLabel, Snackbar, TableSortLabel,
} from '@mui/material';
import { Add, ArrowBack, Edit, AssignmentReturn as RecoverIcon, MoneyOff as WriteOffIcon } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, CurrencyText, StatusChip } from '../../components/accounting';
import CommissionPaymentDialog from '../../components/accounting/CommissionPaymentDialog';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { commissionService, RECIPIENT_TYPES } from '../../services/accounting/commission.service';
import { staffService, EMPLOYMENT_TYPES } from '../../services/accounting/staff.service';
import { serverMessage } from '../../utils/apiConvert';
import { TypeChip } from './Commissions';

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const EMPTY_FORM = { userId: '', type: 'EXTERNAL_AGENT', trnNumber: '', active: true };
// Employees and owners earn as internal staff; everyone else as an external agent.
const recipientTypeFor = (employmentType) => (['EMPLOYEE', 'OWNER'].includes(employmentType) ? 'INTERNAL_STAFF' : 'EXTERNAL_AGENT');

const fieldSx = { InputProps: { sx: { fontSize: fs.sm } }, InputLabelProps: { sx: { fontSize: fs.sm } } };

const CommissionRecipients = () => {
  const navigate = useNavigate();
  const user = sessionUser();
  const canWrite = can(user, 'finance', 'write');
  const [recipients, setRecipients] = useState([]);
  const [members, setMembers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [busy, setBusy] = useState(false);
  const [sortOwed, setSortOwed] = useState(false);
  const [pay, setPay] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setRecipients(await commissionService.getRecipients());
    } catch (err) {
      setError(serverMessage(err, 'Failed to load recipients'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openNew = async () => {
    setForm({ ...EMPTY_FORM });
    setEditing({});
    try {
      const tcId = user.typingCenterId || user.typing_center_id;
      const [res, staff] = await Promise.all([
        ApiService.get(`/api/users/typing-center/${tcId}`),
        staffService.list().catch(() => []),
      ]);
      setMembers(res?.data?.data || []);
      setProfiles(staff || []);
    } catch (err) {
      setMembers([]);
    }
  };
  const openEdit = (r) => {
    setForm({ userId: r.userId, type: r.type, trnNumber: r.trnNumber || '', active: r.active !== false });
    setEditing(r);
  };
  const profileOf = (userId) => profiles.find((p) => p.userId === userId) || null;
  // Picking a member sets the type from their employment type.
  const pickMember = (e) => {
    const userId = e.target.value;
    const p = profileOf(userId);
    setForm((f) => ({
      ...f, userId,
      type: p ? recipientTypeFor(p.employmentType) : f.type,
    }));
  };
  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const data = { type: form.type, trnNumber: form.trnNumber.trim() };
    try {
      if (editing?.id) await commissionService.updateRecipient(editing.id, { ...data, active: form.active });
      else await commissionService.createRecipient({ ...data, userId: form.userId });
      setSnackbar(editing?.id ? 'Recipient updated' : 'Recipient added');
      setEditing(null);
      loadData();
    } catch (err) {
      setError(serverMessage(err, 'Failed to save the recipient'));
    } finally {
      setBusy(false);
    }
  };

  const owed = (r) => Number(r.receivable || 0);
  const rows = sortOwed ? [...recipients].sort((a, b) => owed(b) - owed(a)) : recipients;
  const profiled = new Set(recipients.map((r) => r.userId));
  const candidates = members.filter((m) => !profiled.has(m.id) && m.active !== false);

  const COLUMNS = [
    { key: 'name', label: 'Name', width: '15%', render: (r) => <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>{r.name}</Typography> },
    { key: 'type', label: 'Type', width: '12%', render: (r) => <TypeChip type={r.type} /> },
    { key: 'email', label: 'Email', width: '15%', render: (r) => r.email || '—' },
    { key: 'bankName', label: 'Bank', width: '9%', render: (r) => r.bankName || '—' },
    { key: 'outstanding', label: 'Outstanding', width: '9%', align: 'right', render: (r) => <CurrencyText amount={r.outstanding} /> },
    { key: 'earned', label: 'Total Earned', width: '9%', align: 'right', render: (r) => <CurrencyText amount={r.earned} /> },
    { key: 'paid', label: 'Total Paid', width: '9%', align: 'right', render: (r) => <CurrencyText amount={r.paid} /> },
    { key: 'receivable', align: 'right', width: '9%',
      label: <TableSortLabel active={sortOwed} direction="desc" onClick={() => setSortOwed((s) => !s)} sx={{ fontSize: 'inherit' }}>Owes Back</TableSortLabel>,
      render: (r) => (owed(r) > 0 ? <CurrencyText amount={r.receivable} bold color="error" /> : '—') },
    { key: 'active', label: 'Status', width: '7%', render: (r) => <StatusChip tone={r.active !== false ? 'success' : 'default'} label={r.active !== false ? 'Active' : 'Inactive'} /> },
    { key: 'actions', label: '', width: '6%', align: 'right', render: (r) => canWrite && (
      <Box display="flex" justifyContent="flex-end">
        <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>
        {owed(r) > 0 && <Tooltip title="Recover (recipient returns money)"><IconButton size="small" onClick={() => setPay({ direction: 'RECOVERED', recipient: r })}><RecoverIcon fontSize="small" /></IconButton></Tooltip>}
        {owed(r) > 0 && <Tooltip title="Write off"><IconButton size="small" color="warning" onClick={() => setPay({ direction: 'WRITTEN_OFF', recipient: r })}><WriteOffIcon fontSize="small" /></IconButton></Tooltip>}
      </Box>
    ) },
  ];

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate('/accounting/commissions')}><ArrowBack fontSize="small" /></IconButton>
          <Typography variant="h5" fontWeight={600} color="secondary.main">Commission Recipients</Typography>
        </Box>
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openNew} sx={{ textTransform: 'none', fontSize: fs.sm }}>
            Add Recipient
          </Button>
        )}
      </Box>

      <DataTable columns={COLUMNS} data={rows} loading={loading}
        emptyMessage="No commission recipients configured."
        emptyAction={canWrite ? { label: 'Add your first recipient', onClick: openNew } : undefined} />

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>
          {editing?.id ? `Edit Commission Recipient — ${editing.name}` : 'Add Commission Recipient'}
        </DialogTitle>
        <DialogContent dividers>
          {!editing?.id && (
            <TextField select label="Team member" fullWidth size="small" sx={{ mt: 1, mb: 2 }} required {...fieldSx}
              value={form.userId} onChange={pickMember}
              helperText={candidates.length === 0 ? 'Every active member already has a profile — add the person on the Team page first'
                : 'The type fills in from the member\'s profile. An outside agent is first added as a team member (login optional)'}
              FormHelperTextProps={{ sx: { fontSize: fs.xs } }}>
              {candidates.map((m) => (
                <MenuItem key={m.id} value={m.id} sx={{ fontSize: fs.sm }}>
                  {m.full_name || m.fullName || m.username}{m.email ? ` - ${m.email}` : ''}
                  {profileOf(m.id) && (
                    <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary', ml: 0.75 }}>
                      · {EMPLOYMENT_TYPES[profileOf(m.id).employmentType]}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField select label="Type" fullWidth size="small" sx={{ mt: editing?.id ? 1 : 0, mb: 2 }} required {...fieldSx}
            value={form.type} onChange={setField('type')}>
            {Object.entries(RECIPIENT_TYPES).map(([value, label]) => <MenuItem key={value} value={value} sx={{ fontSize: fs.sm }}>{label}</MenuItem>)}
          </TextField>

          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 2 }}>
            {editing?.id && (editing.bankName || editing.iban)
              ? `Paid to ${[editing.bankName, editing.iban && `•••• ${String(editing.iban).slice(-4)}`].filter(Boolean).join(' ')}. `
              : 'No bank details yet. '}
            Bank details are the person's own record: change them on the Team page.
          </Typography>
          <TextField label="TRN (optional)" fullWidth size="small" {...fieldSx} value={form.trnNumber} onChange={setField('trnNumber')}
            placeholder="TRN-100234567890003" />
          {editing?.id && (
            <FormControlLabel sx={{ mt: 1 }} label={<Typography sx={{ fontSize: fs.sm }}>Active (an inactive recipient earns no new commission)</Typography>}
              control={<Switch size="small" checked={form.active} onChange={setField('active')} />} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy || (!editing?.id && !form.userId) || !form.type} sx={{ textTransform: 'none' }}>
            {editing?.id ? 'Save' : 'Add Recipient'}
          </Button>
        </DialogActions>
      </Dialog>

      <CommissionPaymentDialog open={Boolean(pay)} direction={pay?.direction} recipient={pay?.recipient || null} recipients={recipients}
        onClose={() => setPay(null)}
        onDone={(p) => { setPay(null); setSnackbar(`${p.paymentNumber} recorded`); loadData(); }} />

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default CommissionRecipients;
